import React, { useMemo } from 'react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';
import Icon from '../../../components/AppIcon';

const clampPercent = (value, fallback = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  if (num < 0) return 0;
  if (num > 100) return 100;
  return Math.round(num);
};

const proficiencyToScore = (proficiency) => {
  const proficiencyMap = {
    expert: 95,
    advanced: 85,
    intermediate: 70,
    beginner: 50,
    basic: 40
  };
  return proficiencyMap[(proficiency || '').toLowerCase()] ?? 65;
};

const normalizeRadarEntry = (entry, fallbackName) => ({
  skill: entry?.skill || entry?.name || fallbackName,
  userScore: clampPercent(
    entry?.candidate_score ?? entry?.candidate ?? entry?.your_score ?? entry?.user_score,
    0
  ),
  marketDemand: clampPercent(entry?.market_demand ?? entry?.market ?? entry?.demand, 0),
  justification: entry?.justification || entry?.reason || entry?.explanation || ''
});

const SkillsSpiderChart = ({
  extractedSkills = [],
  missingSkills = [],
  skillsRadar = [],
  role
}) => {
  const radarData = useMemo(() => {
    if (Array.isArray(skillsRadar) && skillsRadar.length > 0) {
      return skillsRadar
        .map((entry, index) => normalizeRadarEntry(entry, `Skill ${index + 1}`))
        .filter((entry) => entry.skill);
    }

    if (!extractedSkills || extractedSkills.length === 0) return [];

    return extractedSkills.slice(0, 10).map((skill, index) => ({
      skill: skill?.skill_name || `Skill ${index + 1}`,
      userScore: clampPercent(proficiencyToScore(skill?.proficiency_level)),
      marketDemand: clampPercent(80) // neutral fallback
    }));
  }, [extractedSkills, skillsRadar]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const userScore = payload.find((item) => item.dataKey === 'userScore')?.value ?? 0;
      const marketScore = payload.find((item) => item.dataKey === 'marketDemand')?.value ?? 0;
      const delta = marketScore - userScore;

      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-medium max-w-xs space-y-2">
          <div>
            <p className="font-medium text-popover-foreground">{label}</p>
            <p className="text-xs text-muted-foreground">
              {delta > 0
                ? `Market demand is ${Math.abs(delta)}% higher than your current level.`
                : delta < 0
                  ? `You are ${Math.abs(delta)}% ahead of market demand.`
                  : 'Perfectly aligned with market expectations.'}
            </p>
          </div>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-muted-foreground">
                {entry?.dataKey === 'userScore' ? 'Your level' : 'Market demand'}:
              </span>
              <span className="font-medium text-popover-foreground">{entry?.value}%</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (!radarData || radarData.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 shadow-subtle">
        <div className="text-center py-12">
          <Icon name="BarChart3" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Skills Data</h3>
          <p className="text-sm text-muted-foreground">
            We couldn’t plot your skills for this role. Paste a richer job description or rerun the
            analysis.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-4 sm:p-6 shadow-subtle">
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Skills Analysis</h3>
          <p className="text-sm text-muted-foreground">
            Your skills vs market demand for {role || 'your target role'}
          </p>
          <p className="text-xs text-muted-foreground/70 italic mt-1">
            * Market demand values are estimated based on industry averages
          </p>
        </div>

        <div className="h-64 sm:h-80 md:h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <PolarGrid stroke="var(--color-border)" />
              <PolarAngleAxis
                dataKey="skill"
                tick={{ fontSize: 12, fill: 'var(--color-muted-foreground)' }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
                tickCount={6}
              />
              <Radar
                name="Your Level"
                dataKey="userScore"
                stroke="var(--color-primary)"
                fill="var(--color-primary)"
                fillOpacity={0.2}
                strokeWidth={2}
              />
              <Radar
                name="Market Demand"
                dataKey="marketDemand"
                stroke="var(--color-secondary)"
                fill="var(--color-secondary)"
                fillOpacity={0.12}
                strokeWidth={2}
                strokeDasharray="5 5"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: 20 }} iconType="line" />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-3 pt-4 border-t border-border">
          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-3 rounded-lg transition-colors">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-2">
                  <Icon
                    name="ChevronRight"
                    size={16}
                    className="text-muted-foreground group-open:rotate-90 transition-transform"
                  />
                  <div>
                    <div className="text-lg font-bold text-primary">
                      {extractedSkills?.length || radarData.length}
                    </div>
                    <div className="text-xs text-muted-foreground">Total Skills</div>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">Click to view all</span>
              </div>
            </summary>
            <div className="mt-2 ml-6 space-y-1 max-h-60 overflow-y-auto">
              {(extractedSkills?.length ? extractedSkills : radarData).map((skill, idx) => (
                <div
                  key={skill?.skill_name || skill?.skill || idx}
                  className="flex items-center justify-between text-xs py-1 px-2 bg-muted/30 rounded"
                >
                  <span className="text-foreground">
                    {skill?.skill_name || skill?.skill || `Skill ${idx + 1}`}
                  </span>
                  <span className="text-muted-foreground capitalize">
                    {skill?.proficiency_level ||
                      `${clampPercent(skill?.userScore ?? skill?.confidence_score ?? 0)}%`}
                  </span>
                </div>
              ))}
            </div>
          </details>

          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-3 rounded-lg transition-colors">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-2">
                  <Icon
                    name="ChevronRight"
                    size={16}
                    className="text-muted-foreground group-open:rotate-90 transition-transform"
                  />
                  <div>
                    <div className="text-lg font-bold text-success">
                      {radarData.filter((skill) => skill.userScore >= 70).length}
                    </div>
                    <div className="text-xs text-muted-foreground">Strong Skills (70%+)</div>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">Click to view all</span>
              </div>
            </summary>
            <div className="mt-2 ml-6 space-y-1">
              {radarData
                .filter((skill) => skill.userScore >= 70)
                .map((skill) => (
                  <div
                    key={`strong-${skill.skill}`}
                    className="flex items-center justify-between text-xs py-1 px-2 bg-success/10 rounded"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="TrendingUp" size={12} className="text-success" />
                      <span className="text-foreground">{skill.skill}</span>
                    </div>
                    <span className="text-muted-foreground">{skill.userScore}%</span>
                  </div>
                ))}
              {radarData.filter((skill) => skill.userScore >= 70).length === 0 && (
                <p className="text-xs text-muted-foreground">No standout strengths yet.</p>
              )}
            </div>
          </details>

          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-3 rounded-lg transition-colors">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-2">
                  <Icon
                    name="ChevronRight"
                    size={16}
                    className="text-muted-foreground group-open:rotate-90 transition-transform"
                  />
                  <div>
                    <div className="text-lg font-bold text-warning">{missingSkills?.length || 0}</div>
                    <div className="text-xs text-muted-foreground">To Develop</div>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">Click to view all</span>
              </div>
            </summary>
            <div className="mt-2 ml-6 space-y-1 max-h-60 overflow-y-auto">
              {Array.isArray(missingSkills) && missingSkills.length > 0 ? (
                missingSkills.slice(0, 50).map((skill, idx) => (
                  <div
                    key={typeof skill === 'string' ? skill : skill?.name || idx}
                    className="flex items-center space-x-2 text-xs py-1 px-2 bg-warning/10 rounded"
                  >
                    <Icon name="AlertCircle" size={12} className="text-warning" />
                    <span className="text-foreground">{typeof skill === 'string' ? skill : skill?.name}</span>
                  </div>
                ))
              ) : (
                <p className="text-xs text-muted-foreground">No missing skills detected.</p>
              )}
              {missingSkills?.length > 50 && (
                <p className="text-xs text-muted-foreground italic py-2">
                  ...and {missingSkills.length - 50} more skills to develop
                </p>
              )}
            </div>
          </details>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border">
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-card-foreground flex items-center space-x-2">
              <Icon name="TrendingUp" size={16} className="text-success" />
              <span>Top Strengths</span>
            </h4>
            <div className="space-y-1">
              {radarData
                .slice()
                .sort((a, b) => b.userScore - a.userScore)
                .slice(0, 3)
                .map((skill) => (
                  <div
                    key={`strength-${skill.skill}`}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={14} className="text-success" />
                      <span className="text-foreground">{skill.skill}</span>
                    </div>
                    <span className="text-muted-foreground">{skill.userScore}%</span>
                  </div>
                ))}
              {radarData.length === 0 && (
                <p className="text-xs text-muted-foreground">No data to display.</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-card-foreground flex items-center space-x-2">
              <Icon name="Target" size={16} className="text-warning" />
              <span>Growth Areas</span>
            </h4>
            <div className="space-y-1">
              {radarData
                .slice()
                .sort((a, b) => (b.marketDemand - b.userScore) - (a.marketDemand - a.userScore))
                .slice(0, 3)
                .map((skill) => (
                  <div
                    key={`gap-${skill.skill}`}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="AlertCircle" size={14} className="text-warning" />
                      <span className="text-foreground">{skill.skill}</span>
                    </div>
                    <span className="text-muted-foreground">
                      -
                      {Math.max(skill.marketDemand - skill.userScore, 0)}%
                    </span>
                  </div>
                ))}
              {radarData.length === 0 && (
                <p className="text-xs text-muted-foreground">No gaps identified.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillsSpiderChart;